import rawData from "../assets/StationsData.json";

const TRAIN_TYPE_ORDER = [
  "passenger",
  "freight",
  "fuel",
  "alignment",
  "civil",
  "other",
];

const trainTypeMeta = {
  passenger: {
    label: "Passenger",
    description: "Local passenger service with frequent stops.",
    color: "#2e7d32",
    accent: "#e8f5e9",
    icon: "directions_subway",
    localizationKey: "passenger",
  },
  freight: {
    label: "Freight",
    description: "Heavy cargo and logistics operations.",
    color: "#ef6c00",
    accent: "#fff3e0",
    icon: "inventory_2",
    localizationKey: "freight",
  },
  fuel: {
    label: "Fuel",
    description: "Petroleum and fuel transport units.",
    color: "#b91c1c",
    accent: "#fee2e2",
    icon: "local_gas_station",
    localizationKey: "fuel",
  },
  alignment: {
    label: "Track alignment",
    description: "Maintenance units aligning railway bars.",
    color: "#0f766e",
    accent: "#ccfbf1",
    icon: "handyman",
    localizationKey: "alignment",
  },
  civil: {
    label: "Civil works",
    description: "Infrastructure and civil work support units.",
    color: "#7c3aed",
    accent: "#ede9fe",
    icon: "engineering",
    localizationKey: "civil",
  },
  other: {
    label: "Other",
    description: "Unclassified rolling stock.",
    color: "#475569",
    accent: "#e2e8f0",
    icon: "train",
    localizationKey: "other",
  },
};

const routeBlueprints = [
  {
    id: "startVertical",
    name: "Basra Gateway",
    subtitle: "Industrial access to the southern ports",
    color: "#f59f00",
    accent: "linear-gradient(120deg, #fff3bf, #ffe066)",
    layout: { lat: 30.35, lng: 47.75, dLat: 0.18, dLng: -0.08 },
  },
  {
    id: "southToNorth",
    name: "South → North Mainline",
    subtitle: "Backbone service connecting Basra to Mosul",
    color: "#4263eb",
    accent: "linear-gradient(120deg, #edf2ff, #dbe4ff)",
    layout: { lat: 30.5, lng: 47.8, dLat: 0.22, dLng: -0.04 },
  },
  {
    id: "west",
    name: "Western Corridor",
    subtitle: "Express link toward Al-Qaim and the border",
    color: "#0ca678",
    accent: "linear-gradient(120deg, #e6fcf5, #c3fae8)",
    layout: { lat: 33.4, lng: 44.3, dLat: 0.04, dLng: -0.32 },
  },
  {
    id: "horizontalWest",
    name: "Al-Anbar Spur",
    subtitle: "Access to desert extraction sites",
    color: "#15aabf",
    accent: "linear-gradient(120deg, #e3fafc, #c5f6fa)",
    layout: { lat: 34.1, lng: 42.9, dLat: 0.02, dLng: -0.24 },
  },
  {
    id: "angle",
    name: "Northwest Ridge",
    subtitle: "Connector through rugged highlands",
    color: "#7950f2",
    accent: "linear-gradient(120deg, #f3f0ff, #d0bfff)",
    layout: { lat: 35.1, lng: 43.5, dLat: 0.14, dLng: -0.18 },
  },
  {
    id: "est",
    name: "Kirkuk Gateway",
    subtitle: "Strategic petroleum distribution route",
    color: "#d6336c",
    accent: "linear-gradient(120deg, #fff0f6, #fcc2d7)",
    layout: { lat: 35.3, lng: 44.6, dLat: 0.12, dLng: 0.21 },
  },
];

const generateCoordinate = (index, layout) => ({
  lat: parseFloat((layout.lat + layout.dLat * index).toFixed(4)),
  lng: parseFloat((layout.lng + layout.dLng * index).toFixed(4)),
});

const extractCoordinate = (station, index, layout) => {
  const lat = Number.parseFloat(station.x ?? station.lat);
  const lng = Number.parseFloat(station.y ?? station.lng);

  if (Number.isFinite(lat) && Number.isFinite(lng)) {
    return { lat, lng };
  }

  return generateCoordinate(index, layout);
};

const normalizeKey = (value) =>
  typeof value === "string" ? value.trim().toLowerCase() : String(value || "");

const resolveTrainTypeKeyFromName = (name) => {
  if (!name || typeof name !== "string") {
    return "other";
  }

  const normalized = normalizeKey(name);

  if (!normalized) {
    return "other";
  }

  if (normalized.includes("dmu")) {
    return "passenger";
  }

  if (normalized.includes("dem")) {
    return "freight";
  }

  if (normalized.includes("dhl")) {
    return "fuel";
  }

  if (normalized.includes("jac") || normalized.includes("mac")) {
    return "alignment";
  }

  if (normalized.includes("روبل")) {
    return "civil";
  }

  return "other";
};

const defaultTrainTypeMeta = () => ({ ...trainTypeMeta.other });

const buildRoutes = (data = rawData) => {
  const stationById = new Map();
  const stationNameMap = new Map();

  const routes = routeBlueprints.map((blueprint) => {
    const sourceStations = data[blueprint.id] || [];
    const stations = sourceStations.map((station, index) => {
      const coordinate = extractCoordinate(station, index, blueprint.layout);
      const entry = {
        id: station.id,
        code: `ST-${station.id}`,
        name: station.name,
        order: index + 1,
        ...coordinate,
        routeId: blueprint.id,
        routeName: blueprint.name,
        routeColor: blueprint.color,
        totalStations: sourceStations.length,
      };

      stationById.set(station.id, entry);
      stationNameMap.set(normalizeKey(station.name), entry);

      return entry;
    });

    return {
      ...blueprint,
      stations,
      totalStations: stations.length,
    };
  });

  return { routes, stationById, stationNameMap };
};

const buildTrains = (assignments = [], stationById) => {
  if (!assignments.length) {
    return [];
  }

  return assignments.map((train, index) => {
    const station = stationById.get(train.station_id);
    const rawName =
      typeof train.name === "string" && train.name.trim() ? train.name : "";
    let typeKey = resolveTrainTypeKeyFromName(rawName);

    if (!rawName) {
      if (typeof train.type === "number") {
        typeKey = train.type === 1 ? "passenger" : "freight";
      } else {
        typeKey = "other";
      }
    }

    const typeMeta = trainTypeMeta[typeKey] || defaultTrainTypeMeta();
    const type = { ...typeMeta, localizationKey: typeMeta.localizationKey || typeKey };
    const now = Date.now();
    const lastMessageAt =
      typeof train.lastMessageAt === "number"
        ? train.lastMessageAt
        : now - Math.floor(Math.random() * 2 * 60 * 60 * 1000);

    return {
      id: train.id,
      name: rawName || `Unit ${train.id}`,
      stationReference: train.station_id,
      typeId: typeKey,
      sequence: index + 1,
      code: `TR-${train.id}`,
      routeId: station?.routeId ?? null,
      routeName: station?.routeName ?? "Unassigned",
      routeColor: station?.routeColor ?? type.color,
      stationId: station?.id ?? null,
      stationName: station?.name ?? "Unknown station",
      fromStationId: station?.id ?? null,
      fromStationName: station?.name ?? null,
      toStationId: station?.id ?? null,
      toStationName: station?.name ?? null,
      lat: station?.lat ?? null,
      lng: station?.lng ?? null,
      course: null,
      type,
      isInsideKnownGeofence: Boolean(station),
      progress:
        station && station.totalStations > 1
          ? Math.round(((station.order - 1) / (station.totalStations - 1)) * 100)
          : null,
      lastMessageAt,
    };
  });
};

const computeAggregates = (routes, trains) => {
  const totals = {
    routes: routes.length,
    stations: routes.reduce((acc, route) => acc + route.totalStations, 0),
    trains: trains.length,
  };

  const trainsByRoute = routes.reduce((acc, route) => {
    acc[route.id] = trains.filter((train) => train.routeId === route.id);
    return acc;
  }, {});

  const trainTypeTotals = trains.reduce((acc, train) => {
    const key = train.typeId;
    acc[key] = acc[key] ? acc[key] + 1 : 1;
    return acc;
  }, {});

  return { totals, trainsByRoute, trainTypeTotals };
};

const buildStaticNetwork = () => {
  const { routes, stationById, stationNameMap } = buildRoutes(rawData);
  const trains = buildTrains(rawData.trains || [], stationById);
  const aggregates = computeAggregates(routes, trains);

  return {
    routes,
    trains,
    ...aggregates,
    stationById,
    stationNameMap,
    trainTypeMeta,
  };
};

export {
  buildRoutes,
  buildStaticNetwork,
  buildTrains,
  computeAggregates,
  defaultTrainTypeMeta,
  normalizeKey,
  resolveTrainTypeKeyFromName,
  TRAIN_TYPE_ORDER,
  routeBlueprints,
  trainTypeMeta,
};

export default buildStaticNetwork;
